import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, MapPin, Shield, AlertTriangle, CheckCircle, XCircle, Search, Filter, Eye, ArrowLeft, Plus, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LoginAttemptDetails } from './LoginAttemptDetails';
import { LoginAttemptsMap } from './LoginAttemptsMap';
import { LiveSessionsView } from './LiveSessionsView';
import { SessionHealthCheck } from './SessionHealthCheck';
import { AdminHeader } from '../dashboard/AdminHeader';

interface LoginAttempt {
  id: string;
  user_id?: string | null;
  phone_number?: string | null;
  email?: string | null;
  attempt_type: string;
  success: boolean;
  ip_address?: string | null;
  user_agent?: string | null;
  location_data?: any;
  device_fingerprint?: any;
  session_data?: any;
  risk_score: number;
  created_at: string;
  updated_at: string;
}

interface LoginAttemptsPageProps {
  userName: string | null;
  networkStatus: "checking" | "verified" | "failed";
  onNetworkCheck: () => void;
  onNewOrder: () => void;
  onPriceCheck: () => void;
  onEmployeeClockIn: () => void;
  onEmployeeManagement: () => void;
  onViewBackend: () => void;
  onCreditReports: () => void;
  onCostMonitor: () => void;
  onMasterProducts: () => void;
  onAdminSettings: () => void;
  onArchivedOrders: () => void;
  onSignOutAdmin: () => void;
  onLoginAttempts: () => void;
  onBack: () => void;
}

export const LoginAttemptsPage = ({ 
  userName,
  networkStatus,
  onNetworkCheck,
  onNewOrder,
  onPriceCheck,
  onEmployeeClockIn,
  onEmployeeManagement,
  onViewBackend,
  onCreditReports,
  onCostMonitor,
  onMasterProducts,
  onAdminSettings,
  onArchivedOrders,
  onSignOutAdmin,
  onLoginAttempts,
  onBack 
}: LoginAttemptsPageProps) => {
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState<LoginAttempt | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'success' | 'failed'>('all');
  const [filterAttemptType, setFilterAttemptType] = useState<'all' | 'sms' | 'email' | 'password'>('all');
  const [showLiveSessions, setShowLiveSessions] = useState(false);
  const [showHealthCheck, setShowHealthCheck] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchLoginAttempts();
    fetchActiveSessions();
  }, []);

  const fetchLoginAttempts = async () => {
    try {
      setLoading(true);
      console.log('Fetching login attempts...');
      const { data, error } = await supabase
        .from('login_attempts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      console.log('Login attempts fetched:', data);
      const formattedData = (data as LoginAttempt[]) || [];
      console.log('Formatted data:', formattedData);
      setAttempts(formattedData);
      
      if (!data || data.length === 0) {
        console.log('No login attempts found in database');
      } else {
        console.log(`Found ${data.length} login attempts`);
      }
    } catch (error: any) {
      console.error('Error fetching login attempts:', error);
      toast({
        title: "Error",
        description: "Failed to load login attempts data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Active Sessions management
  const [activeSessions, setActiveSessions] = useState<any[]>([]);
  const [showActiveSessions, setShowActiveSessions] = useState(false);

  const fetchActiveSessions = async () => {
    try {
      console.log('Fetching active sessions...');
      const { data, error } = await supabase
        .from('login_sessions')
        .select('*')
        .is('duration_minutes', null) // Active sessions don't have duration yet
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('Active sessions fetched:', data);
      setActiveSessions(data || []);
      
      if (!data || data.length === 0) {
        console.log('No active sessions found in database');
      }
    } catch (error: any) {
      console.error('Error fetching active sessions:', error);
      toast({
        title: "Error", 
        description: "Failed to load active sessions",
        variant: "destructive"
      });
    }
  };

  const forceLogout = async (sessionId: string) => {
    try {
      // In a real implementation, this would invalidate the session
      await supabase
        .from('login_sessions')
        .update({ 
          duration_minutes: Math.floor((Date.now() - new Date().getTime()) / 60000),
          last_activity: new Date().toISOString()
        })
        .eq('id', sessionId);

      toast({
        title: "Success",
        description: "User session terminated",
        variant: "default"
      });
      
      await fetchActiveSessions();
    } catch (error: any) {
      console.error('Error terminating session:', error);
      toast({
        title: "Error",
        description: "Failed to terminate session",
        variant: "destructive"
      });
    }
  };

  const filteredAttempts = attempts.filter(attempt => {
    // Handle search with proper type checking
    const matchesSearch = !searchTerm || 
      attempt.phone_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attempt.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (attempt.ip_address && attempt.ip_address.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesSuccessFilter = filterType === 'all' || 
      (filterType === 'success' && attempt.success) ||
      (filterType === 'failed' && !attempt.success);
    
    const matchesTypeFilter = filterAttemptType === 'all' || 
      attempt.attempt_type === filterAttemptType;

    return matchesSearch && matchesSuccessFilter && matchesTypeFilter;
  });

  const successCount = attempts.filter(a => a.success).length;
  const failedCount = attempts.filter(a => !a.success).length;
  const highRiskCount = attempts.filter(a => a.risk_score > 50).length;

  const getRiskBadgeVariant = (score: number) => {
    if (score >= 75) return 'destructive';
    if (score >= 50) return 'default';
    if (score >= 25) return 'secondary';
    return 'outline';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 75) return 'Critical';
    if (score >= 50) return 'High';
    if (score >= 25) return 'Medium';
    return 'Low';
  };

  if (selectedAttempt) {
    return (
      <LoginAttemptDetails 
        attempt={selectedAttempt} 
        onBack={() => setSelectedAttempt(null)} 
      />
    );
  }

  if (showHealthCheck) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        <AdminHeader
          userName={userName}
          networkStatus={networkStatus}
          onNetworkCheck={onNetworkCheck}
          onNewOrder={onNewOrder}
          onPriceCheck={onPriceCheck}
          onEmployeeClockIn={onEmployeeClockIn}
          onEmployeeManagement={onEmployeeManagement}
          onViewBackend={onViewBackend}
          onScannerTest={() => {}}
          onSwitchToStaff={onSignOutAdmin}
          onSuppliersManagement={() => {}}
          onCreditReports={onCreditReports}
          onCostMonitor={onCostMonitor}
          onMasterProducts={onMasterProducts}
          onAdminSettings={onAdminSettings}
          onArchivedOrders={onArchivedOrders}
          onLoginAttempts={onLoginAttempts}
        />
        <div className="container mx-auto p-6">
          <SessionHealthCheck onBack={() => setShowHealthCheck(false)} />
        </div>
      </div>
    );
  }

  if (showLiveSessions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        <AdminHeader
          userName={userName}
          networkStatus={networkStatus}
          onNetworkCheck={onNetworkCheck}
          onNewOrder={onNewOrder}
          onPriceCheck={onPriceCheck}
          onEmployeeClockIn={onEmployeeClockIn}
          onEmployeeManagement={onEmployeeManagement}
          onViewBackend={onViewBackend}
          onScannerTest={() => {}}
          onSwitchToStaff={onSignOutAdmin}
          onSuppliersManagement={() => {}}
          onCreditReports={onCreditReports}
          onCostMonitor={onCostMonitor}
          onMasterProducts={onMasterProducts}
          onAdminSettings={onAdminSettings}
          onArchivedOrders={onArchivedOrders}
          onLoginAttempts={onLoginAttempts}
        />
        <div className="container mx-auto p-6">
          <LiveSessionsView onBack={() => setShowLiveSessions(false)} />
        </div>
      </div>
    );
  }

  if (showMap) {
    return (
      <LoginAttemptsMap 
        attempts={filteredAttempts} 
        onBack={() => setShowMap(false)}
        onSelectAttempt={setSelectedAttempt}
        userName={userName}
        networkStatus={networkStatus}
        onNetworkCheck={onNetworkCheck}
        onNewOrder={onNewOrder}
        onPriceCheck={onPriceCheck}
        onEmployeeClockIn={onEmployeeClockIn}
        onEmployeeManagement={onEmployeeManagement}
        onViewBackend={onViewBackend}
        onCreditReports={onCreditReports}
        onCostMonitor={onCostMonitor}
        onMasterProducts={onMasterProducts}
        onAdminSettings={onAdminSettings}
        onArchivedOrders={onArchivedOrders}
        onSignOutAdmin={onSignOutAdmin}
        onLoginAttempts={onLoginAttempts}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <AdminHeader
        userName={userName}
        networkStatus={networkStatus}
        onNetworkCheck={onNetworkCheck}
        onNewOrder={onNewOrder}
        onPriceCheck={onPriceCheck}
        onEmployeeClockIn={onEmployeeClockIn}
        onEmployeeManagement={onEmployeeManagement}
        onViewBackend={onViewBackend}
        onScannerTest={() => {}}
        onSwitchToStaff={onSignOutAdmin}
        onSuppliersManagement={() => {}}
        onCreditReports={onCreditReports}
        onCostMonitor={onCostMonitor}
        onMasterProducts={onMasterProducts}
        onAdminSettings={onAdminSettings}
        onArchivedOrders={onArchivedOrders}
        onLoginAttempts={onLoginAttempts}
      />
      
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-blue-800">Login Attempts Monitor</h2>
                <p className="text-blue-600">Analyze authentication activities and security events</p>
              </div>
              <div className="bg-blue-100 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-blue-700">Security Dashboard</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div className="flex space-x-2">
              <Button 
                onClick={() => setShowHealthCheck(true)} 
                variant="outline"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Health Check
              </Button>
              <Button 
                onClick={() => setShowLiveSessions(true)} 
                variant="default"
              >
                <Globe className="mr-2 h-4 w-4" />
                Live Sessions
              </Button>
              <Button 
                onClick={() => setShowActiveSessions(!showActiveSessions)} 
                variant={showActiveSessions ? "default" : "outline"}
              >
                <Shield className="mr-2 h-4 w-4" />
                {showActiveSessions ? 'Login Attempts' : 'Active Sessions'}
              </Button>
              <Button onClick={() => setShowMap(true)} variant="outline">
                <MapPin className="mr-2 h-4 w-4" />
                Map View
              </Button>
              <Button onClick={fetchLoginAttempts} variant="outline">
                <Search className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Attempts</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{attempts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{successCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{failedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Risk</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{highRiskCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <Input
                placeholder="Phone, email, or IP address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Attempts</SelectItem>
                  <SelectItem value="success">Successful</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select value={filterAttemptType} onValueChange={(value: any) => setFilterAttemptType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="password">Password</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login Attempts or Active Sessions */}
      {showActiveSessions ? (
        <Card>
          <CardHeader>
            <CardTitle>Active User Sessions</CardTitle>
            <CardDescription>
              Currently logged in users - {activeSessions.length} active sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeSessions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No active sessions found.
              </div>
            ) : (
              <div className="space-y-3">
                {activeSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <Badge variant="outline">Active</Badge>
                      </div>
                      <div>
                        <div className="font-medium">
                          Session {session.session_token?.slice(-8) || 'Unknown'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          User ID: {session.user_id || 'Unknown'} • Started: {new Date(session.created_at).toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Last Activity: {session.last_activity ? new Date(session.last_activity).toLocaleString() : 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-muted-foreground">
                        Duration: {Math.floor((Date.now() - new Date(session.created_at).getTime()) / 60000)}m
                      </div>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => forceLogout(session.id)}
                      >
                        Force Logout
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Recent Login Attempts</CardTitle>
            <CardDescription>
              Showing {filteredAttempts.length} of {attempts.length} total attempts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredAttempts.length === 0 ? (
              <div className="text-center py-8 space-y-4">
                <div className="text-muted-foreground">
                  {attempts.length === 0 
                    ? "No login attempts found in the database."
                    : "No login attempts found matching your filters."
                  }
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAttempts.map((attempt) => (
                  <div
                    key={attempt.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedAttempt(attempt)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {attempt.success ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600" />
                        )}
                        <Badge variant={attempt.success ? 'default' : 'destructive'}>
                          {attempt.success ? 'Success' : 'Failed'}
                        </Badge>
                      </div>
                      <div>
                        <div className="font-medium">
                          {attempt.phone_number || attempt.email || 'Unknown user'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {attempt.attempt_type?.toUpperCase() || 'UNKNOWN'} • {attempt.ip_address?.toString() || 'Unknown IP'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <Badge variant={getRiskBadgeVariant(attempt.risk_score)}>
                        {getRiskLabel(attempt.risk_score)} ({attempt.risk_score})
                      </Badge>
                      {attempt.location_data?.latitude && (
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div className="text-sm text-muted-foreground">
                        {new Date(attempt.created_at).toLocaleDateString()} {new Date(attempt.created_at).toLocaleTimeString()}
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
        </div>
      </div>
    </div>
  );
};